import { Button, Input, message } from 'antd'
import imageCompression from 'browser-image-compression'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import $api from '../../../../shared/api/api'
import { PageSection } from '../../../../shared/ui/PageSection'
import cls from './ReviewsSection.module.scss'

interface Review {
    id: string
    name: string
    text: string
    imageUrl?: string | null
    createdAt: string
}

const serverUrl = (process.env.REACT_APP_API_URL ?? '').replace(/\/api\/?$/, '')

const ReviewsSection: React.FC = () => {
    const { t } = useTranslation()
    const [reviews, setReviews] = useState<Review[]>([])

    const [name, setName] = useState('')
    const [text, setText] = useState('')
    const [imageFile, setImageFile] = useState<File | null>(null)
    const [preview, setPreview] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        $api.get<Review[]>('/reviews').then((res) => setReviews(res.data)).catch(() => {})
    }, [])

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.files?.[0] ?? null
        if (!raw) { setImageFile(null); setPreview(null); return }
        const compressed = await imageCompression(raw, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1280,
            useWebWorker: true
        })
        const file = new File([compressed], raw.name, { type: compressed.type })
        setImageFile(file)
        setPreview(URL.createObjectURL(file))
    }

    const handleRemoveImage = () => {
        setImageFile(null)
        setPreview(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const handleSubmit = async () => {
        if (!name.trim() || !text.trim()) {
            message.warning(t('reviews.form.required'))
            return
        }

        setSubmitting(true)
        try {
            const fd = new FormData()
            fd.append('name', name.trim())
            fd.append('text', text.trim())
            if (imageFile) fd.append('image', imageFile)

            await $api.post('/reviews', fd)
            message.success(t('reviews.form.success'))
            setName('')
            setText('')
            setImageFile(null)
            setPreview(null)
            if (fileInputRef.current) fileInputRef.current.value = ''
        } catch {
            message.error(t('reviews.form.error'))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <PageSection
            title={t('reviews.title')}
            align="start"
            style={{ backgroundColor: 'var(--bg-base)' }}
        >
            {reviews.length > 0 ? (
                <div className={cls.grid}>
                    {reviews.map((review) => {
                        const imgSrc = review.imageUrl
                            ? review.imageUrl.startsWith('http')
                                ? review.imageUrl
                                : `${serverUrl}${review.imageUrl}`
                            : null

                        return (
                            <div key={review.id} className={cls.card}>
                                <div className={cls.cardHeader}>
                                    {imgSrc ? (
                                        <img src={imgSrc} alt={review.name} className={cls.avatar} />
                                    ) : (
                                        <div className={cls.avatarInitial}>
                                            {review.name.charAt(0)}
                                        </div>
                                    )}
                                    <p className={cls.name}>{review.name}</p>
                                </div>
                                <p className={cls.text}>{review.text}</p>
                            </div>
                        )
                    })}
                </div>
            ) : (
                <p className={cls.emptyText}>{t('reviews.empty')}</p>
            )}

            <div className={cls.formWrap}>
                <p className={cls.formTitle}>{t('reviews.form.title')}</p>

                <div className={cls.formField}>
                    <label className={cls.label}>{t('reviews.form.name')} *</label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('reviews.form.namePlaceholder')}
                        className={cls.input}
                    />
                </div>

                <div className={cls.formField}>
                    <label className={cls.label}>{t('reviews.form.text')} *</label>
                    <Input.TextArea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={t('reviews.form.textPlaceholder')}
                        rows={4}
                        className={cls.input}
                    />
                </div>

                <div className={cls.formField}>
                    <label className={cls.label}>{t('reviews.form.image')}</label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className={cls.hiddenInput}
                    />
                    {preview ? (
                        <div className={cls.previewWrap}>
                            <img src={preview} alt="preview" className={cls.previewImg} />
                            <button
                                type="button"
                                className={cls.removeBtn}
                                onClick={handleRemoveImage}
                                aria-label="Remove image"
                            >
                                ×
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className={cls.uploadZone}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <span className={cls.plusIcon}>+</span>
                            <span className={cls.uploadHint}>{t('reviews.form.uploadHint')}</span>
                        </button>
                    )}
                </div>

                <Button
                    type="primary"
                    className={cls.submitBtn}
                    loading={submitting}
                    onClick={handleSubmit}
                >
                    {t('reviews.form.submit')}
                </Button>
            </div>
        </PageSection>
    )
}

export default ReviewsSection
